import { translations } from '@aws-amplify/ui-react';
import { CogNeedTextField } from '@cogneed/react-utils';
import { LoadingButton } from '@mui/lab';
import {
  Button,
  DialogActions, DialogContent, DialogContentText, DialogTitle, Typography,
} from '@mui/material';
import { I18n } from 'aws-amplify/utils';
import React from 'react';
import MutationError from '../../Common/MutationError';
import config from '../../config';
import StyledDialog from './styles';

I18n.putVocabularies(translations);
I18n.putVocabulariesForLanguage('fr', {
  'sso.title': 'Se connecter avec SSO',
  'sso.description': "Ceci vous connectera à CogNeed avec le fournisseur d'identité paramétré pour votre organisation.",
  'sso.cancel': 'Annuler',
  'sso.forgetEmail': 'Oublier mon email',
  'sso.initialized': 'Authentification SSO initialisée. Connexion en cours...',
});
I18n.putVocabulariesForLanguage('en', {
  'sso.title': 'Sign in with SSO',
  'sso.description': 'This will log you in CogNeed using the identity provider configured for your organisation.',
  'sso.cancel': 'Cancel',
  'sso.forgetEmail': 'Forget my email',
  'sso.initialized': 'SSO authentication initialized. Logging in...',
});

const ssoFakeError = 'Error in SAML response processing: Already found an entry for username';

export default function SingleSignOnDialog(props: { open: boolean, onClose: () => void, ssoError?: { message: string, email?: string } }) {
  const { open, onClose, ssoError } = props;
  const [email, setEmail] = React.useState(ssoError?.email ?? localStorage.getItem('email') ?? '');
  const ref = React.useRef<HTMLFormElement>(null);
  const isSsoInitialized = React.useMemo(() => ssoError?.message.startsWith(ssoFakeError), [ssoError]);

  React.useEffect(() => {
    if (isSsoInitialized) {
      if (ref.current) {
        ref.current.submit();
      } else {
        const interval = setTimeout(() => { ref.current?.submit(); }, 600);
        return () => clearInterval(interval);
      }
    }
    return () => { };
  }, [isSsoInitialized, ref.current]);

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        {I18n.get('sso.title')}
      </DialogTitle>
      <form action={`${config.Cognito.CognitoUri}/sso`} method="post" onSubmit={() => { localStorage.setItem('email', email); }} ref={ref}>
        <DialogContent>
          <DialogContentText>
            {I18n.get('sso.description')}
          </DialogContentText>
          <CogNeedTextField
            fullWidth
            placeholder="Email"
            value={email}
            onChange={(e) => { setEmail(e.target.value); }}
            id="email"
            name="email"
            autoFocus={email === ''}
          />
          {localStorage.getItem('email') && !isSsoInitialized && (
            <Button className="forget" onClick={() => { localStorage.removeItem('email'); setEmail(''); }} variant="text">
              {I18n.get('sso.forgetEmail')}
            </Button>
          )}
          {ssoError && (isSsoInitialized
            ? <Typography className="sso-success">{I18n.get('sso.initialized')}</Typography>
            : <MutationError>{ssoError.message}</MutationError>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="info" variant="outlined">
            {I18n.get('sso.cancel')}
          </Button>
          <LoadingButton
            disabled={email === ''}
            color="primary"
            variant="contained"
            type="submit"
            loading={!!isSsoInitialized}
          >
            {I18n.get('login.title')}
          </LoadingButton>
        </DialogActions>
      </form>
    </StyledDialog>
  );
}
